import React, { useState, useEffect} from 'react';
import Navbar from '../Navbar/Nabvar';
import Footer from '../Footer/Footer';
import { Box, Typography, Container, Card, CardContent, CardHeader, Divider, Button, Grid, CardMedia } from '@mui/material';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { useCart } from '../Context/CardContext';
import jsPDF from 'jspdf';
import { mock_methods } from '../KnowHow/mock_methods';
import {mock_trends} from '../Trends/mock_trends';
import QuoteBox from '../Quote/QuoteBox';

const Warenkorb = () => {
    const primaryBlue = '#1F1B9E';
    const { items, removeItem } = useCart();
    const trends = items.filter(item => item.type === 'trend');
    const methods = items.filter(item => item.type === 'method');

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const cardStyle = {
        width: '100%',
        maxWidth: '1200px',
        mx: 'auto',
        mb: 4,
        '& .MuiCardHeader-root': {
            textAlign: 'center',
        },
        '& .MuiCardContent-root': {
            textAlign: 'center',
        }
    };

    const wavyBackgroundStyle = {
        '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `
                linear-gradient(to bottom right, 
                    rgba(240, 245, 255, 0.9),
                    rgba(230, 240, 255, 0.9)
                )
            `,
            zIndex: 0
        }
    };

    const generateTrendPDF = async () => {
        const doc = new jsPDF();

        // Lade das Layout-Bild als Hintergrund
        let layoutImage: HTMLImageElement | null = null;
        try {
            const img = new Image();
            img.crossOrigin = 'Anonymous';
            img.src = 'layout/layout.png';

            await new Promise<void>((resolve, reject) => {
                img.onload = () => {
                    layoutImage = img;
                    resolve();
                };
                img.onerror = reject;
            });
        } catch (e) {
            console.warn("Layout konnte nicht geladen werden, verwende Standard-Layout", e);
        }

        // Erweiterte Margen für Header (Logos/Text oben) und Footer (Partner unten)
        const topMargin = 45; // Mehr Platz für Header
        const bottomMargin = 35; // Mehr Platz für Footer
        const leftMargin = 20;
        const pageHeight = doc.internal.pageSize.height;
        const pageWidth = doc.internal.pageSize.width;
        const maxYPosition = pageHeight - bottomMargin;

        const today = new Date();
        const formattedDate = today.toLocaleDateString('de-DE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        });

        // Funktion zum Hinzufügen des Layout-Hintergrunds
        const addLayoutBackground = () => {
            if (layoutImage) {
                try {
                    // Layout als Hintergrund einfügen (volle Seitengröße)
                    doc.addImage(layoutImage, 'PNG', 0, 0, pageWidth, pageHeight);
                } catch (e) {
                    console.error("Fehler beim Hinzufügen des Layouts:", e);
                }
            }
        };

        // Erste Seite mit Layout
        addLayoutBackground();

        let yPosition = topMargin;
        doc.setFontSize(20);
        doc.text(`Ihr Trendkorb vom ${formattedDate}`, leftMargin, yPosition);
        yPosition += 15;

        const groupedTrends = trends.reduce((acc, trend) => {
            const megatrend = mock_trends.find(mt =>
                mt.subtrends.some(st => st.id === trend.id)
            );

            if (megatrend) {
                // @ts-ignore
                if (!acc[megatrend.id]) {
                    // @ts-ignore
                    acc[megatrend.id] = {
                        megatrend: megatrend,
                        subtrends: []
                    };
                }

                const subtrend = megatrend.subtrends.find(st => st.id === trend.id);
                // @ts-ignore
                if (subtrend && !acc[megatrend.id].subtrends.some(st => st.id === subtrend.id)) {
                    // @ts-ignore
                    acc[megatrend.id].subtrends.push(subtrend);
                }
            }
            return acc;
        }, {});

        let isFirstGroup = true;
        Object.values(groupedTrends).forEach((group, groupIndex) => {
            if (!isFirstGroup) {
                doc.addPage();
                addLayoutBackground(); // Layout auf neuer Seite
                yPosition = topMargin;
            } else {
                isFirstGroup = false;
            }

            doc.setFontSize(16);
            // @ts-ignore
            doc.text(`Megatrend: ${group.megatrend.title}`, leftMargin, yPosition);
            yPosition += 10;

            // @ts-ignore
            group.subtrends.forEach((subtrend, index) => {
                const textLines2 = doc.splitTextToSize(subtrend.text, 120).length;
                const estimatedHeight = 15 + (textLines2 * 6) + 10;

                if (yPosition + estimatedHeight > maxYPosition) {
                    doc.addPage();
                    addLayoutBackground(); // Layout auf neuer Seite
                    yPosition = topMargin;
                }

                doc.setFontSize(14);
                doc.text(`Subtrend: ${subtrend.title}`, leftMargin, yPosition);
                yPosition += 10;

                const imgWidth = 50;
                const imgHeight = 50;
                const textWidth = 120;
                const initialYPosition = yPosition;

                let imageLoaded = false;
                try {
                    imageLoaded = !!subtrend.image;
                } catch (e) {
                    console.error("Fehler beim Überprüfen des Bildes:", e);
                }

                doc.setFontSize(12);
                const splitText = doc.splitTextToSize(subtrend.text, textWidth);
                const textHeight = splitText.length * 6;

                if (yPosition + textHeight > maxYPosition) {
                    doc.addPage();
                    addLayoutBackground(); // Layout auf neuer Seite
                    yPosition = topMargin;
                }

                const textLines = splitText.length;
                for (let i = 0; i < textLines; i++) {
                    const line = splitText[i];
                    const words = line.trim().split(/\s+/);
                    const isLastLine = (i === textLines - 1);

                    if (yPosition + (i * 6) > maxYPosition) {
                        doc.addPage();
                        addLayoutBackground(); // Layout auf neuer Seite
                        yPosition = topMargin;
                        i = 0;
                    }

                    if (isLastLine || words.length === 1) {
                        doc.text(line, leftMargin, yPosition + (i * 6));
                    } else {
                        // @ts-ignore
                        const totalWordsWidth = words.reduce((acc, word) => acc + doc.getTextWidth(word), 0);
                        const spaceWidth = (textWidth - totalWordsWidth) / (words.length - 1);
                        let xPos = leftMargin;
                        // @ts-ignore
                        words.forEach((word, index) => {
                            doc.text(word, xPos, yPosition + (i * 6));
                            if (index < words.length - 1) {
                                xPos += doc.getTextWidth(word) + spaceWidth;
                            }
                        });
                    }
                }

                if (imageLoaded) {
                    try {
                        const imgX = leftMargin + textWidth + 10;

                        if (initialYPosition + imgHeight <= maxYPosition) {
                            doc.addImage(subtrend.image, "png", imgX, initialYPosition, imgWidth, imgHeight);
                        } else {
                            doc.addPage();
                            addLayoutBackground(); // Layout auf neuer Seite
                            doc.addImage(subtrend.image, "png", imgX, topMargin, imgWidth, imgHeight);
                        }
                    } catch (e) {
                        console.error("Fehler beim Laden des Bildes:", e);
                    }
                }

                yPosition += Math.max(textHeight, imgHeight) + 5;
            });
        });

        doc.save('trendkorb.pdf');
    };

    const downloadMethods = async () => {
        if (methods.length === 0) return;

        const today = new Date();
        const formattedDate = today.toLocaleDateString('de-DE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        }).replace(/\./g, '-');

        // Erstelle eine separate PDF für jede Methode
        for (const item of methods) {
            const methodDetails = mock_methods.find(m => m.id === item.id);

            if (methodDetails && methodDetails.image_download) {
                try {
                    // Erstelle neue PDF für diese Methode
                    const doc = new jsPDF({
                        orientation: 'landscape'
                    });

                    // Lade das Bild
                    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
                        const image = new Image();
                        image.crossOrigin = 'Anonymous';
                        image.src = methodDetails.image_download;

                        image.onload = () => resolve(image);
                        image.onerror = () => reject(new Error(`Fehler beim Laden von ${methodDetails.title}`));
                    });

                    // Berechne Dimensionen für die Seite
                    const pageWidth = doc.internal.pageSize.getWidth();
                    const pageHeight = doc.internal.pageSize.getHeight();

                    // Berechne Skalierung, um das Bild optimal auf die Seite zu passen
                    const imgRatio = img.width / img.height;
                    const pageRatio = pageWidth / pageHeight;

                    let imgWidth, imgHeight;
                    if (imgRatio > pageRatio) {
                        // Bild ist breiter - passe an Breite an
                        imgWidth = pageWidth;
                        imgHeight = pageWidth / imgRatio;
                    } else {
                        // Bild ist höher - passe an Höhe an
                        imgHeight = pageHeight;
                        imgWidth = pageHeight * imgRatio;
                    }

                    // Zentriere das Bild
                    const xOffset = (pageWidth - imgWidth) / 2;
                    const yOffset = (pageHeight - imgHeight) / 2;

                    doc.addImage(img, 'JPEG', xOffset, yOffset, imgWidth, imgHeight);

                    // Speichere die PDF mit dem Namen der Methode
                    const sanitizedTitle = methodDetails.title
                        .replace(/[^a-zA-Z0-9äöüÄÖÜß\s-]/g, '')
                        .replace(/\s+/g, '_');

                    doc.save(`${sanitizedTitle}_${formattedDate}.pdf`);

                    // Kleine Verzögerung zwischen den Downloads, damit der Browser mitkommt
                    await new Promise(resolve => setTimeout(resolve, 300));

                } catch (error) {
                    console.error(`Fehler bei Methode ${methodDetails.title}:`, error);
                    alert(`Fehler beim Erstellen der PDF für "${methodDetails.title}"`);
                }
            }
        }
    };

    return (
        <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
            <Navbar />

            {/* Top section with wavy background - Reduzierte Höhe */}
            <Box
                sx={{
                    position: 'relative',
                    py: 4,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '200px',
                    paddingTop: "75px",
                    ...wavyBackgroundStyle
                }}
            >
                <Box sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
                    <Typography
                        variant="h1"
                        sx={{
                            color: primaryBlue,
                            mb: 2,
                            fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                            fontWeight: 'bold'
                        }}
                    >
                        INNOVATIONS-WARENKÖRBE
                    </Typography>

                    <Typography
                        variant="body1"
                        sx={{
                            color: primaryBlue,
                            maxWidth: '1200px',
                            mx: 'auto',
                            px: 2,
                            fontSize: { xs: '1rem', sm: '1.2rem', md: '1.5rem' }
                        }}
                    >
                        Ihr Zukunftspaket ist bereit! Laden Sie ausgewählte Trend-Kombinationen als PDF herunter – kompakt gebündelt, strategisch relevant und direkt nutzbar für Ihre Geschäftsmodell-Innovation.
                    </Typography>
                </Box>
            </Box>

            {/* Hiking image section - Reduzierte Höhe */}
            {/*<Box*/}
            {/*    sx={{*/}
            {/*        width: '100%',*/}
            {/*        height: { xs: '250px', sm: '350px', md: '450px' },*/}
            {/*        backgroundImage: 'url("/old/wandern_kontakt.png")',*/}
            {/*        backgroundSize: 'cover',*/}
            {/*        backgroundPosition: 'center',*/}
            {/*    }}*/}
            {/*/>*/}

            {/* Trendkorb Section - Kompakteres Design */}
            <Box sx={{
                backgroundColor: 'white',
                width: '100%',
                py: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
            }}>
                <ShoppingCartIcon style={{ color: primaryBlue, fontSize: '4rem', marginBottom: 0.5 }} />

                <Typography
                    variant="body1"
                    sx={{
                        color: primaryBlue,
                        maxWidth: '1200px',
                        mx: 'auto',
                        px: 2,
                        fontSize: { xs: '1.3rem', sm: '1.5rem', md: '1.8rem' },
                        mb: 2
                    }}
                >
                    Sie haben {trends.length} Trends in Ihrem Trendkorb
                </Typography>

                <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
                    <Grid container spacing={{ xs: 2, sm: 2, md: 3 }} sx={{ justifyContent: 'center' }}>
                        {trends.map((item, index) => (
                            <Grid item xs={12} sm={6} md={3} key={`${item.id}-${index}`}>
                                <Card sx={{ border: '2px solid black' }}>
                                    <CardMedia
                                        component="img"
                                        height="250"
                                        image={item.image}
                                        alt={item.title}
                                    />
                                    <CardContent sx={{
                                        backgroundColor: 'white',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        py: 1.5
                                    }}>
                                        <Typography sx={{ color: 'black', mb: 1, fontSize: '0.95rem' }}>
                                            {item.title}
                                        </Typography>
                                        <Button
                                            variant="outlined"
                                            onClick={() => removeItem(item.id, 'trend')}
                                            sx={{
                                                mt: 1,
                                                color: primaryBlue,
                                                borderColor: primaryBlue,
                                                borderRadius: '24px',
                                                padding: '6px 24px',
                                                fontSize: '0.95rem',
                                                width: '180px',
                                                backgroundColor: 'white',
                                                '&:hover': {
                                                    borderColor: primaryBlue,
                                                    backgroundColor: 'rgba(31, 27, 158, 0.04)'
                                                }
                                            }}
                                        >
                                            Löschen
                                        </Button>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Container>

                {trends.length > 0 && (
                    <Button
                        variant="outlined"
                        onClick={generateTrendPDF}
                        sx={{
                            mt: 2,
                            color: primaryBlue,
                            borderColor: primaryBlue,
                            borderRadius: '24px',
                            padding: '8px 32px',
                            fontSize: '1.1rem',
                            marginTop: '20px',
                            '&:hover': {
                                borderColor: primaryBlue,
                                backgroundColor: 'rgba(31, 27, 158, 0.04)'
                            }
                        }}
                    >
                        Download Trendkorb
                    </Button>
                )}
            </Box>

            {/* Puffer zwischen den Warenkörben - Minimaler Abstand */}
            <Box
                sx={{
                    position: 'relative',
                    py: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '60px',
                    ...wavyBackgroundStyle
                }}
            />

            {/* Methodenkorb Section - Kompakteres Design */}
            <Box sx={{
                backgroundColor: 'white',
                width: '100%',
                py: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
            }}>
                <ShoppingCartIcon style={{ color: primaryBlue, fontSize: '4rem', marginBottom: 0.5 }} />

                <Typography
                    variant="body1"
                    sx={{
                        color: primaryBlue,
                        maxWidth: '1200px',
                        mx: 'auto',
                        px: 2,
                        fontSize: { xs: '1.3rem', sm: '1.5rem', md: '1.8rem' },
                        mb: 2
                    }}
                >
                    Sie haben {methods.length} Methoden in Ihrem Methodenkorb
                </Typography>

                <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
                    <Grid container spacing={{ xs: 2, sm: 2, md: 3 }} sx={{ justifyContent: 'center' }}>
                        {methods.map((item, index) => (
                            <Grid item xs={12} sm={6} md={3} key={`${item.id}-${index}`}>
                                <Card sx={{ border: '2px solid black' }}>
                                    <CardMedia
                                        component="img"
                                        height="250"
                                        image={item.image}
                                        alt={item.title}
                                    />
                                    <CardContent sx={{
                                        backgroundColor: 'white',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        py: 1.5
                                    }}>
                                        <Typography sx={{ color: 'black', mb: 1, fontSize: '0.95rem' }}>
                                            {item.title}
                                        </Typography>
                                        <Button
                                            variant="outlined"
                                            onClick={() => removeItem(item.id, 'method')}
                                            sx={{
                                                mt: 1,
                                                color: primaryBlue,
                                                borderColor: primaryBlue,
                                                borderRadius: '24px',
                                                padding: '6px 24px',
                                                fontSize: '0.95rem',
                                                width: '180px',
                                                backgroundColor: 'white',
                                                '&:hover': {
                                                    borderColor: primaryBlue,
                                                    backgroundColor: 'rgba(31, 27, 158, 0.04)'
                                                }
                                            }}
                                        >
                                            Löschen
                                        </Button>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Container>

                {methods.length > 0 && (
                    <Button
                        variant="outlined"
                        onClick={downloadMethods}
                        sx={{
                            mt: 2,
                            color: primaryBlue,
                            borderColor: primaryBlue,
                            borderRadius: '24px',
                            padding: '8px 32px',
                            fontSize: '1.1rem',
                            marginTop: '20px',
                            marginBottom: '50px',
                            '&:hover': {
                                borderColor: primaryBlue,
                                backgroundColor: 'rgba(31, 27, 158, 0.04)'
                            }
                        }}
                    >
                        Download Methodenkorb
                    </Button>
                )}
            </Box>

            {/*<QuoteBox*/}
            {/*    text="„Methoden geben Halt, wo Ideen noch fliegen.“"*/}
            {/*    maxWidth="1200px"*/}
            {/*    hasOverlay={true}*/}
            {/*/>*/}

            <Footer />
        </Box>
    );
};

export default Warenkorb;